/**
 * DüV (Düngeverordnung) Legal References
 *
 * Key paragraphs from the German Fertilizer Ordinance
 * that are relevant for compliance control.
 */

import type { LegalReference } from '@hofsync/design-system';

/**
 * DüV § 3 - Düngebedarfsermittlung
 */
export const DUEV_3: LegalReference = {
  id: 'duev-3',
  law: 'DüV',
  lawFullName: 'Düngeverordnung',
  paragraph: '§ 3',
  title: 'Grundsätze für die Anwendung von Düngemitteln',
  shortCitation: 'DüV § 3',
  quote:
    '(1) Vor dem Aufbringen von wesentlichen Nährstoffmengen an Stickstoff oder Phosphat ' +
    'mit Düngemitteln, Bodenhilfsstoffen, Kultursubstraten oder Pflanzenhilfsmitteln hat ' +
    'der Betriebsinhaber den Düngebedarf der Kultur für jeden Schlag oder jede ' +
    'Bewirtschaftungseinheit nach den Vorgaben des § 4 zu ermitteln.',
  effectiveDate: '26. Mai 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/d_v_2017/__3.html',
  relevanceExplanation:
    'Die Düngebedarfsermittlung ist vor jeder wesentlichen Düngungsmaßnahme durchzuführen.',
};

/**
 * DüV § 4 Abs. 1 - Düngebedarfsermittlung Details
 */
export const DUEV_4_1: LegalReference = {
  id: 'duev-4-1',
  law: 'DüV',
  lawFullName: 'Düngeverordnung',
  paragraph: '§ 4 Abs. 1',
  title: 'Ermittlung des Düngebedarfs an Stickstoff',
  shortCitation: 'DüV § 4 Abs. 1',
  quote:
    '(1) Der Düngebedarf für Stickstoff ist im Falle von Ackerland für jeden Schlag oder ' +
    'jede Bewirtschaftungseinheit vor dem Aufbringen wesentlicher Nährstoffmengen, in der ' +
    'Regel im Frühjahr, zu ermitteln und aufzuzeichnen.',
  effectiveDate: '26. Mai 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/d_v_2017/__4.html',
  relevanceExplanation:
    'Die Stickstoff-Bedarfsermittlung muss vor der Hauptdüngung im Frühjahr erfolgen.',
};

/**
 * DüV § 4 Abs. 4 - Bodenuntersuchungspflicht
 */
export const DUEV_4_4: LegalReference = {
  id: 'duev-4-4',
  law: 'DüV',
  lawFullName: 'Düngeverordnung',
  paragraph: '§ 4 Abs. 4',
  title: 'Bodenuntersuchung',
  shortCitation: 'DüV § 4 Abs. 4',
  quote:
    '(4) Der Betriebsinhaber hat vor dem Aufbringen wesentlicher Nährstoffmengen an ' +
    'Stickstoff oder Phosphat die im Boden verfügbaren Nährstoffmengen auf jedem Schlag ' +
    'oder jeder Bewirtschaftungseinheit – außer auf Dauergrünlandflächen – durch ' +
    'Untersuchung repräsentativer Bodenproben bestimmen zu lassen. Die Bodenuntersuchung ' +
    'auf verfügbaren Stickstoff hat jährlich, die Bodenuntersuchung auf verfügbares ' +
    'Phosphat mindestens alle sechs Jahre zu erfolgen.',
  effectiveDate: '26. Mai 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/d_v_2017/__4.html',
  relevanceExplanation:
    'Bodenproben auf Phosphat müssen mindestens alle 6 Jahre durchgeführt werden.',
};

/**
 * DüV § 6 - Sperrfristen
 */
export const DUEV_6: LegalReference = {
  id: 'duev-6',
  law: 'DüV',
  lawFullName: 'Düngeverordnung',
  paragraph: '§ 6',
  title: 'Sperrfristen',
  shortCitation: 'DüV § 6',
  quote:
    '(1) Das Aufbringen von Düngemitteln mit wesentlichem Gehalt an Stickstoff auf ' +
    'Ackerland ist in der Zeit vom 1. Oktober bis zum 31. Januar verboten. Für Grünland ' +
    'gilt die Sperrfrist vom 1. November bis zum 31. Januar. Gülle, Jauche, Geflügelkot ' +
    'und andere Düngemittel mit wesentlichem Stickstoffgehalt dürfen auf Ackerland vom ' +
    '1. Oktober bis zum 31. Januar nicht aufgebracht werden.',
  effectiveDate: '26. Mai 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/d_v_2017/__6.html',
  relevanceExplanation:
    'Die Einhaltung der Sperrfristen wird bei DÜV-Kontrollen geprüft.',
};

/**
 * DüV § 6 Abs. 8 - Ausbringungsverbot auf gefrorenen Boden
 */
export const DUEV_6_8: LegalReference = {
  id: 'duev-6-8',
  law: 'DüV',
  lawFullName: 'Düngeverordnung',
  paragraph: '§ 6 Abs. 8',
  title: 'Ausbringungsverbot auf gefrorenen Boden',
  shortCitation: 'DüV § 6 Abs. 8',
  quote:
    '(8) Das Aufbringen von Düngemitteln, Bodenhilfsstoffen, Kultursubstraten und ' +
    'Pflanzenhilfsmitteln mit wesentlichem Gehalt an Stickstoff oder Phosphat ist nicht ' +
    'zulässig, wenn der Boden überschwemmt, wassergesättigt, gefroren oder schneebedeckt ist.',
  effectiveDate: '26. Mai 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/d_v_2017/__6.html',
  relevanceExplanation:
    'Düngung auf gefrorenem oder schneebedecktem Boden ist grundsätzlich verboten.',
};

/**
 * DüV § 10 Abs. 1 - Aufzeichnungspflichten
 */
export const DUEV_10_1: LegalReference = {
  id: 'duev-10-1',
  law: 'DüV',
  lawFullName: 'Düngeverordnung',
  paragraph: '§ 10 Abs. 1',
  title: 'Aufzeichnungen',
  shortCitation: 'DüV § 10 Abs. 1',
  quote:
    '(1) Der Betriebsinhaber hat spätestens zwei Tage nach jeder Düngungsmaßnahme nach § 3 ' +
    'Absatz 1 Satz 1 folgende Angaben aufzuzeichnen: ' +
    '1. eindeutige Bezeichnung des Schlages, der Bewirtschaftungseinheit oder der ' +
    'Zusammenfassung von Schlägen nach § 3 Absatz 2 Satz 3, ' +
    '2. Größe des Schlages, der Bewirtschaftungseinheit oder der Zusammenfassung von Schlägen, ' +
    '3. Art und Menge des aufgebrachten Stoffs, ' +
    '4. Menge an Stickstoff und Phosphat und Datum der Aufbringung.',
  effectiveDate: '26. Mai 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/d_v_2017/__10.html',
  relevanceExplanation:
    'Alle Düngungsmaßnahmen müssen innerhalb von 2 Tagen dokumentiert werden.',
};

/**
 * DüV § 10 Abs. 2 - Jährlicher Nährstoffvergleich
 */
export const DUEV_10_2: LegalReference = {
  id: 'duev-10-2',
  law: 'DüV',
  lawFullName: 'Düngeverordnung',
  paragraph: '§ 10 Abs. 2',
  title: 'Jährlicher betrieblicher Nährstoffvergleich',
  shortCitation: 'DüV § 10 Abs. 2',
  quote:
    '(2) Der Betriebsinhaber hat jährlich spätestens bis zum 31. März nach Abschluss des ' +
    'Düngejahres einen betrieblichen Nährstoffvergleich für Stickstoff und Phosphat ' +
    'aufzustellen. Der Nährstoffvergleich ist nach den Vorgaben der Anlage 5 zu erstellen.',
  effectiveDate: '26. Mai 2017',
  lastAmended: '28. April 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/d_v_2017/__10.html',
  relevanceExplanation:
    'Der jährliche Nährstoffvergleich muss bis zum 31.03. des Folgejahres erstellt werden.',
};

/**
 * All DüV legal references indexed by ID
 */
export const DUEV_LEGAL_REFS: Record<string, LegalReference> = {
  'duev-3': DUEV_3,
  'duev-4-1': DUEV_4_1,
  'duev-4-4': DUEV_4_4,
  'duev-6': DUEV_6,
  'duev-6-8': DUEV_6_8,
  'duev-10-1': DUEV_10_1,
  'duev-10-2': DUEV_10_2,
};
